import { Button, Form, Input, Select, Upload, Image, UploadFile, GetProp, UploadProps, App } from 'antd';
import { TeamModel, UserModel } from '../../dto/reporting-models';
import { useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { RcFile } from 'antd/es/upload';

interface TeamFormProps {
  team: TeamModel;
  teamMembers: UserModel[];
  users: UserModel[];
  usersWithoutTeam?: UserModel[];
  onSubmit: (team: TeamModel, members: UserModel[]) => void;
  cancel: () => void;
}

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

const getBase64 = (file: FileType): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

export const TeamForm = ({ team, teamMembers, users, usersWithoutTeam, onSubmit, cancel }: TeamFormProps) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [currentTeam, setCurrentTeam] = useState<TeamModel>(team);
  const [currentMembers, setCurrentMembers] = useState<UserModel[]>(teamMembers);
  const [usersToSelectFrom] = useState<UserModel[]>([...(usersWithoutTeam || []), ...teamMembers.map((member) => ({ id: member.id, fullName: member.fullName } as UserModel))]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [fileList, setFileList] = useState<UploadFile[]>(
    team.icon
      ? [
          {
            uid: '-1',
            name: `${team.id}.png`,
            status: 'done',
            url: decodeURIComponent(team.icon),
          },
        ]
      : []
  );

  const uploadButton = (
    <button style={{ border: 0, background: 'none' }} type="button">
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </button>
  );

  function onFinish(): void {
    if (fileList[0] !== null && fileList[0] !== undefined && fileList[0].name !== `${team.id}.png`) {
      const reader = new FileReader();
      reader.readAsDataURL(fileList[0].originFileObj as File);
      reader.onload = () => {
        const base64String = reader.result as string;
        team.icon = base64String;
        onSubmit(currentTeam, currentMembers);
        form.resetFields();
      };
      reader.onerror = () => {
        message.error('Failed to read file');
      };
      return;
    }
    onSubmit(currentTeam, currentMembers);
    form.resetFields();
  }

  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as FileType);
    }
    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
  };

  const validateImageDimensions = (file: RcFile): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      const img = document.createElement('img');
      img.src = URL.createObjectURL(file);

      img.onload = () => {
        if (img.width === 100 && img.height === 100) {
          resolve(true); // Allow upload
        } else {
          message.error('Image must be exactly 100x100 pixels!');
          resolve(false); // Reject upload
        }
      };

      img.onerror = () => {
        message.error('Failed to load image.');
        reject(false);
      };
    });
  };

  const handleChange: UploadProps['onChange'] = async ({ fileList: newFileList }) => {
    if (newFileList.length > 0 && newFileList[0].type === 'image/png' && (await validateImageDimensions(newFileList[0].originFileObj as RcFile))) {
      setFileList(newFileList);
    } else {
      setFileList([]);
    }
  };

  return (
    <Form form={form} onFinish={onFinish} layout="vertical" style={{ marginBottom: '20px' }}>
      <Form.Item required label="Name" name="name" initialValue={currentTeam.name}>
        <Input placeholder="Team name" onChange={(e) => setCurrentTeam({ ...currentTeam, name: e.target.value })} />
      </Form.Item>
      <Form.Item required label="Team Lead" name="lead" initialValue={users.find((user) => user.id === currentTeam.leadId)?.fullName}>
        <Select
          showSearch={true}
          value={currentTeam.leadId}
          placeholder="Select team lead"
          filterOption={(input, option) => (option?.label.toLowerCase() ?? '').includes(input.toLowerCase())}
          onChange={(value) => setCurrentTeam({ ...currentTeam, leadId: value })}
          options={users.map((user) => {
            return { label: user.fullName, value: user.id };
          })}
        />
      </Form.Item>
      <Form.Item label="Observers" name="observers" initialValue={currentTeam.observers}>
        <Select
          placeholder="Select observers"
          value={currentTeam.observers}
          mode="multiple"
          filterOption={(input, option) => (option?.label.toLowerCase() ?? '').includes(input.toLowerCase())}
          onChange={(selectedIds) => {
            setCurrentTeam({ ...currentTeam, observers: selectedIds });
          }}
          options={users.map((user) => {
            return { label: user.fullName, value: user.id };
          })}
        />
      </Form.Item>
      <Form.Item
        label="Team members"
        name="members"
        initialValue={currentMembers.map((member) => {
          return { label: member.fullName, value: member.id };
        })}
      >
        {/* TODO: Add a validation that the team lead is a member of the team */}
        <Select
          placeholder="Select team members"
          value={currentMembers.map((member) => member.id)}
          mode="multiple"
          filterOption={(input, option) => (option?.label.toLowerCase() ?? '').includes(input.toLowerCase())}
          onChange={(selectedIds) => {
            const updatedMembers = selectedIds
              .map((id) => {
                const user = users.find((user) => user.id === id);
                return user;
              })
              .filter((user): user is UserModel => user !== undefined);
            setCurrentMembers(updatedMembers);
          }}
          options={usersToSelectFrom.map((user) => {
            return { label: user.fullName, value: user.id };
          })}
        />
      </Form.Item>
      <Form.Item label="Icon" name="icon">
        <Upload
          beforeUpload={(file) => {
            const isPNG = file.type === 'image/png';
            if (!isPNG) {
              message.error(`${file.name} is not a png file`);
            }
            return false;
          }}
          listType="picture-circle"
          fileList={fileList}
          onPreview={handlePreview}
          onChange={handleChange}
        >
          {fileList.length >= 1 ? null : uploadButton}
        </Upload>
        {previewImage && (
          <Image
            wrapperStyle={{ display: 'none' }}
            preview={{
              visible: previewOpen,
              onVisibleChange: (visible) => setPreviewOpen(visible),
              afterOpenChange: (visible) => !visible && setPreviewImage(''),
            }}
            src={previewImage}
          />
        )}
      </Form.Item>
      <div className="bg-gray-50 p-4 rounded-md" style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
        <Button onClick={cancel}>Cancel</Button>
        <Button type="primary" htmlType="submit">
          Save
        </Button>
      </div>
    </Form>
  );
};
